// Markdown in the chat, as in voitta-bookmarklet: marked (GFM), sanitised by DOMPurify. SVG renders inline (a raw
// <svg>…</svg> block or an ```svg fence), images show (markdown ![](…) and data: URIs), and ```mermaid fences become
// diagrams. Mermaid is large, so it loads only when a message has a diagram, and its colours follow the app's theme.
import DOMPurify from "dompurify";
import { marked, type Tokens } from "marked";
import { memo, useEffect, useMemo, useRef } from "react";

const renderer = new marked.Renderer();
const code = renderer.code.bind(renderer);
renderer.code = function (token: Tokens.Code) {
  const lang = (token.lang || "").trim().toLowerCase();
  if (lang === "mermaid") return `<div class="mermaid-pending" data-mermaid="${toB64(token.text)}"></div>`;
  if (lang === "svg") return `<div class="md-svg">${token.text}</div>`;
  return code(token);
};
renderer.link = function ({ href, title, tokens }: Tokens.Link) {
  const t = title ? ` title="${title}"` : "";
  return `<a href="${href}"${t} target="_blank" rel="noopener noreferrer">${this.parser.parseInline(tokens)}</a>`;
};

// CommonMark does not know <svg> as a block, and breaks: true would turn each newline inside one into <br>.
const svgBlock = {
  name: "svgBlock",
  level: "block" as const,
  start: (src: string) => { const i = src.search(/<svg[\s>]/i); return i >= 0 ? i : undefined; },
  tokenizer(src: string) {
    const m = /^<svg\b[\s\S]*?<\/svg>\s*/i.exec(src);
    return m ? { type: "svgBlock", raw: m[0], text: m[0].trimEnd() } : undefined;
  },
  renderer: (token: { text: string }) => `<div class="md-svg">${token.text}</div>`,
};

marked.use({ renderer, extensions: [svgBlock], gfm: true, breaks: true });

export function renderMarkdown(text: string): string {
  const html = marked.parse(text, { async: false }) as string;
  return DOMPurify.sanitize(html, {
    ADD_ATTR: ["target", "rel", "xmlns", "xmlns:xlink", "xlink:href", "preserveAspectRatio", "viewBox"],
    ADD_DATA_URI_TAGS: ["img"],
  });
}

// ------------------------------------------------------------------ mermaid
type Mermaid = typeof import("mermaid").default;
let mermaidP: Promise<Mermaid> | null = null;
let mermaidTheme = "";
const cache = new Map<string, string>();
let n = 0;

function themeVars() {
  const v = (name: string) => getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return {
    background: v("--surface"), mainBkg: v("--surface-2"), secondBkg: v("--surface-2"), tertiaryColor: v("--surface-2"),
    primaryColor: v("--surface-2"), primaryTextColor: v("--fg"), secondaryTextColor: v("--fg"), textColor: v("--fg"),
    tertiaryTextColor: v("--muted"), primaryBorderColor: v("--muted"), secondaryBorderColor: v("--muted"),
    tertiaryBorderColor: v("--rule"), lineColor: v("--muted"), noteBkgColor: v("--surface-2"),
    noteTextColor: v("--fg"), noteBorderColor: v("--rule"),
  };
}

async function getMermaid(): Promise<Mermaid> {
  mermaidP ??= import("mermaid").then((m) => m.default);
  const m = await mermaidP;
  const vars = themeVars(), key = JSON.stringify(vars);
  if (key !== mermaidTheme) {                     // first use, or the theme changed: render with the new colours
    m.initialize({ startOnLoad: false, securityLevel: "strict", theme: "base", themeVariables: vars,
      fontFamily: "Inter, system-ui, sans-serif", flowchart: { htmlLabels: false } });
    mermaidTheme = key;
    cache.clear();
  }
  return m;
}

async function hydrate(root: HTMLElement | null): Promise<void> {
  const pending = root ? Array.from(root.querySelectorAll<HTMLElement>("[data-mermaid]")) : [];
  if (!pending.length) return;
  const m = await getMermaid();
  await Promise.all(pending.map(async (el) => {
    const src = fromB64(el.dataset.mermaid || "");
    let svg = cache.get(src);
    if (!svg) {
      try { svg = (await m.render(`mmd-${++n}`, src)).svg; cache.set(src, svg); }
      catch (e) {
        if (!el.isConnected) return;
        const pre = document.createElement("pre");
        pre.className = "mermaid-error";
        pre.textContent = `diagram error: ${e instanceof Error ? e.message : String(e)}`;
        el.replaceWith(pre);
        return;
      }
    }
    if (!el.isConnected) return;
    const wrap = document.createElement("div");
    wrap.className = "mermaid";
    wrap.innerHTML = svg;
    el.replaceWith(wrap);
  }));
}

// rendered once per text: a redraw of the row around it does not parse it again
// Text still being written: an unclosed code block is closed for now, and a diagram (mermaid, SVG) waits for its
// final text behind a placeholder, so a half-written one neither errors nor flickers.
export function settleStreaming(text: string): string {
  let t = text;
  if (((t.match(/^```/gm) || []).length) % 2 === 1) t += "\n```";
  t = t.replace(/^```(mermaid|svg)[^\n]*\n[\s\S]*?^```/gm, "\n*diagram…*\n");
  const open = t.lastIndexOf("<svg");
  if (open >= 0 && t.indexOf("</svg>", open) < 0) t = `${t.slice(0, open)}*diagram…*`;
  return t;
}

export const Markdown = memo(function Markdown({ text, className = "", streaming = false }: { text: string; className?: string;
  streaming?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const html = useMemo(() => renderMarkdown(streaming ? settleStreaming(text) : text), [text, streaming]);
  useEffect(() => { if (!streaming) void hydrate(ref.current); }, [html, streaming]);
  return <div ref={ref} className={`md ${className}`} dangerouslySetInnerHTML={{ __html: html }} />;
});

function toB64(s: string): string {
  let bin = "";
  for (const b of new TextEncoder().encode(s)) bin += String.fromCharCode(b);
  return btoa(bin);
}
function fromB64(b: string): string {
  const bin = atob(b), out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return new TextDecoder().decode(out);
}
